/** Camera + microphone helpers, tuned for modern desktop and mobile browsers. */

export type FacingMode = 'user' | 'environment';

export interface MediaError {
  code: 'denied' | 'not-found' | 'in-use' | 'insecure' | 'unsupported' | 'unknown';
  message: string;
}

/**
 * getUserMedia only exists in secure contexts. Mobile users hitting a plain
 * http:// dev server is the single most common "it doesn't work" report.
 */
export function mediaSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

export function isSecure(): boolean {
  return window.isSecureContext;
}

export function isMobile(): boolean {
  if (navigator.userAgentData?.mobile !== undefined) return navigator.userAgentData.mobile;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}

export function isIOS(): boolean {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // iPadOS 13+ reports itself as a Mac, but Macs have no touch screen.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function constraints(facing: FacingMode): MediaStreamConstraints {
  return {
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    video: {
      facingMode: isMobile() ? { ideal: facing } : undefined,
      width: { ideal: 1280, max: 1920 },
      height: { ideal: 720, max: 1080 },
      frameRate: { ideal: 30, max: 60 },
      aspectRatio: { ideal: 16 / 9 },
    },
  };
}

export async function requestMedia(facing: FacingMode = 'user'): Promise<MediaStream> {
  if (!mediaSupported()) {
    throw toMediaError(
      isSecure()
        ? new DOMException('unsupported', 'NotSupportedError')
        : new DOMException('insecure', 'SecurityError'),
    );
  }

  try {
    return await navigator.mediaDevices.getUserMedia(constraints(facing));
  } catch (error) {
    // Some laptops refuse the ideal resolution — retry with the bare minimum
    // before giving up, and fall back to audio-only as a last resort.
    if (error instanceof DOMException && error.name === 'OverconstrainedError') {
      return navigator.mediaDevices.getUserMedia({ audio: true, video: true });
    }
    throw toMediaError(error);
  }
}

export function toMediaError(error: unknown): MediaError {
  const name = error instanceof DOMException ? error.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      if (!isSecure()) {
        return {
          code: 'insecure',
          message: 'Phidio needs HTTPS to use your camera. Open the site over a secure connection.',
        };
      }
      return {
        code: 'denied',
        message: 'Camera and microphone access was blocked. Enable it in your browser settings and try again.',
      };
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return { code: 'not-found', message: 'No camera or microphone found on this device.' };
    case 'NotReadableError':
    case 'TrackStartError':
      return {
        code: 'in-use',
        message: 'Your camera is busy in another app or tab. Close it and try again.',
      };
    case 'NotSupportedError':
      return {
        code: 'unsupported',
        message: 'This browser cannot do video calls. Try the latest Chrome, Safari, Edge or Firefox.',
      };
    default:
      return {
        code: 'unknown',
        message: error instanceof Error ? error.message : 'Could not start your camera.',
      };
  }
}

export async function listCameras(): Promise<MediaDeviceInfo[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices.filter((device) => device.kind === 'videoinput');
}

export function stopStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}

declare global {
  interface Navigator {
    userAgentData?: { mobile?: boolean };
  }
}
